import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useProject } from '../../context/ProjectContext';
import { getEcho } from '../../echo';
import teamService from '../../services/teamService';
import projectService from '../../services/projectService';
import api from '../../api';
import { notify } from '../CustomToast';
import {
    FaTimes,
    FaEye,
    FaPaperPlane
} from 'react-icons/fa';
import userIcon from '../../assets/User.svg';
import stackSimpleIcon from '../../assets/StackSimple.svg';
import hourglassLowIcon from '../../assets/HourglassLow.svg';
import fileTextIcon from '../../assets/FileText.svg';
import clockIcon from '../../assets/Clock.svg';
import caretDoubleUpIcon from '../../assets/CaretDoubleUp.svg';
import calendarDotsIcon from '../../assets/CalendarDots.svg';
import arrowRightIcon from '../../assets/ArrowRight.svg';
import listChecksIcon from '../../assets/listChecks.svg';
import statusCompleteIcon from '../../assets/status-complete.svg';
import statusHoldIcon from '../../assets/status-hold.svg';
import statusTodoIcon from '../../assets/status-todo.svg';
import statusReopenIcon from '../../assets/status-reopen.svg';
import statusProgressIcon from '../../assets/status-prog.svg';
import downArrowIcon from '../../assets/down-arrow.svg';
import sendIcon from '../../assets/paper-plane-right-bold 2.svg';
import UploadedDocumentsModal from './UploadedDocumentsModal';
import './TaskDetailsModal.css';

// Status and Priority options
const STATUS_OPTIONS = [
    { value: 'todo', label: 'TO DO', icon: statusTodoIcon },
    { value: 'in_progress', label: 'IN PROGRESS', icon: statusProgressIcon },
    { value: 'reopen', label: 'REOPEN', icon: statusReopenIcon },
    { value: 'on_hold', label: 'HOLD', icon: statusHoldIcon },
    { value: 'done', label: 'COMPLETED', icon: statusCompleteIcon },
];

const PRIORITY_OPTIONS = [
    { value: 'low', label: 'LOW' },
    { value: 'medium', label: 'MEDIUM' },
    { value: 'high', label: 'HIGH' },
    { value: 'urgent', label: 'URGENT' },
];

const TaskDetailsModal = ({ isOpen, onClose, task, focusChat = false }) => {
    const { activeProject, addComment, comments, fetchTask, receiveRealtimeComment, removeRealtimeComment, updateTaskStatus, updateTaskPriority, tasks } = useProject();
    const [newComment, setNewComment] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Local task state to track updates
    const [currentTask, setCurrentTask] = useState(task);

    // Dropdown state for status and priority
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
    const statusDropdownRef = useRef(null);
    const priorityDropdownRef = useRef(null);

    // Mention feature state
    const [teamMembers, setTeamMembers] = useState([]);
    const [showMentionDropdown, setShowMentionDropdown] = useState(false);
    const [mentionSearch, setMentionSearch] = useState('');
    const [mentionStartIndex, setMentionStartIndex] = useState(-1);
    const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
    const inputRef = useRef(null);

    // Pagination state
    const [paginatedComments, setPaginatedComments] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [isLoadingComments, setIsLoadingComments] = useState(false);
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const COMMENTS_PER_PAGE = 20;
    const isInitialLoad = useRef(true);

    // Documents modal state
    const [showDocumentsModal, setShowDocumentsModal] = useState(false);
    const [taskDetailsLoading, setTaskDetailsLoading] = useState(false);

    // Update current task when tasks array changes (sync counts/status/priority)
    useEffect(() => {
        if (isOpen && task?.id) {
            const updatedTask = tasks.find(t => t.id === task.id);
            if (updatedTask) {
                setCurrentTask(prev => ({
                    ...prev,
                    ...updatedTask,
                    // Preserve attachments and comments if they were already loaded
                    attachments: prev?.attachments || updatedTask.attachments,
                    comments: prev?.comments || updatedTask.comments,
                    // Ensure status and priority are always current
                    status: updatedTask.status,
                    priority: updatedTask.priority
                }));
            }
        }
    }, [tasks, isOpen]);

    // Fetch full task details when modal opens to get attachments and other data
    useEffect(() => {
        if (isOpen && task?.id) {
            const loadTaskDetails = async () => {
                setTaskDetailsLoading(true);
                try {
                    const fullTask = await fetchTask(task.id);
                    if (fullTask) {
                        setCurrentTask(fullTask);
                    }
                } catch (error) {
                    console.error('Failed to fetch full task details:', error);
                } finally {
                    setTaskDetailsLoading(false);
                }
            };
            loadTaskDetails();
        }
    }, [isOpen, task?.id]);

    // Focus chat input when focusChat prop is true — depends on currentTask being set
    useEffect(() => {
        if (isOpen && focusChat && currentTask) {
            const timer = setTimeout(() => {
                inputRef.current?.focus();
            }, 50);
            return () => clearTimeout(timer);
        }
    }, [isOpen, focusChat, currentTask]);

    // Load initial comments when modal opens
    useEffect(() => {
        if (isOpen && task?.id) {
            isInitialLoad.current = true;
            loadInitialComments();
        }
    }, [isOpen, task?.id]);

    // Load initial comments (latest page)
    const loadInitialComments = async () => {
        if (!task?.id) return;

        try {
            setIsLoadingComments(true);
            setPaginatedComments([]);
            setCurrentPage(1);

            const response = await projectService.getComments(task.id, {
                page: 1,
                per_page: COMMENTS_PER_PAGE
            });

            const commentsData = response?.data?.comments || [];
            const pagination = response?.data?.pagination || {};

            setPaginatedComments(commentsData);
            setTotalPages(pagination.last_page || 1);
            setCurrentPage(1);
            setHasMore(pagination.current_page < pagination.last_page);

            // Scroll to bottom after loading
            setTimeout(() => {
                scrollToBottom();
                isInitialLoad.current = false;
            }, 100);
        } catch (error) {
            console.error('Failed to load comments:', error);
        } finally {
            setIsLoadingComments(false);
        }
    };

    // Handle real-time comment updates
    useEffect(() => {
        if (isOpen && comments.length > 0 && !isInitialLoad.current) {
            const taskComments = comments.filter(c => c.task_id === task.id);
            const currentPaginatedIds = new Set(paginatedComments.map(c => c.id));

            // Check if there's a new comment from real-time update
            const newComments = taskComments.filter(c => !currentPaginatedIds.has(c.id));

            if (newComments.length > 0 && paginatedComments.length > 0) {
                // Append new comments to the bottom
                setPaginatedComments(prev => [...prev, ...newComments]);
                setTimeout(() => {
                    scrollToBottom();
                }, 100);
            }
        }
    }, [comments]);

    // Scroll to bottom function
    const scrollToBottom = () => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    // Handle scroll for pagination
    const handleScroll = useCallback(() => {
        const container = messagesContainerRef.current;
        if (!container || isLoadingComments || !hasMore) return;

        // Check if scrolled to top (with 50px threshold)
        if (container.scrollTop < 50) {
            loadMoreComments();
        }
    }, [isLoadingComments, hasMore]);

    // Load more comments when scrolling up
    const loadMoreComments = useCallback(async () => {
        if (isLoadingComments || !hasMore || !task?.id) return;

        const nextPage = currentPage + 1;
        if (nextPage > totalPages) return;

        setIsLoadingComments(true);

        try {
            const response = await projectService.getComments(task.id, {
                page: nextPage,
                per_page: COMMENTS_PER_PAGE
            });

            const olderComments = response?.data?.comments || [];
            const pagination = response?.data?.pagination || {};

            // Store scroll position before adding comments
            const container = messagesContainerRef.current;
            const previousScrollHeight = container?.scrollHeight || 0;

            setPaginatedComments(prev => [...olderComments, ...prev]);
            setCurrentPage(nextPage);
            setHasMore(pagination.current_page < pagination.last_page);

            // Restore scroll position after DOM updates
            setTimeout(() => {
                if (container) {
                    const newScrollHeight = container.scrollHeight;
                    container.scrollTop = newScrollHeight - previousScrollHeight;
                }
            }, 0);
        } catch (error) {
            console.error('Failed to load more comments:', error);
        } finally {
            setIsLoadingComments(false);
        }
    }, [isLoadingComments, hasMore, currentPage, totalPages, task?.id]);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
                setShowStatusDropdown(false);
            }
            if (priorityDropdownRef.current && !priorityDropdownRef.current.contains(event.target)) {
                setShowPriorityDropdown(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    // Fetch team members for @mention feature
    useEffect(() => {
        const fetchTeamMembers = async () => {
            if (isOpen && activeProject?.team_id) {
                try {
                    const response = await teamService.members(activeProject.team_id);
                    setTeamMembers(response?.data?.members || []);
                } catch (error) {
                    console.error('Failed to fetch team members:', error);
                }
            }
        };
        fetchTeamMembers();
    }, [isOpen, activeProject?.team_id]);

    // Filter team members based on search query
    const filteredMembers = teamMembers.filter(member =>
        member.name?.toLowerCase().includes(mentionSearch.toLowerCase())
    );

    // Handle input change and detect @ mentions
    const handleInputChange = useCallback((e) => {
        const value = e.target.value;
        const cursorPos = e.target.selectionStart;
        setNewComment(value);

        // Find the @ symbol before cursor
        const textBeforeCursor = value.substring(0, cursorPos);
        const lastAtIndex = textBeforeCursor.lastIndexOf('@');

        if (lastAtIndex !== -1) {
            // Check if there's a space before @ (or @ is at start)
            const charBeforeAt = lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : ' ';
            if (charBeforeAt === ' ' || lastAtIndex === 0) {
                const searchText = textBeforeCursor.substring(lastAtIndex + 1);
                // Only show dropdown if no space after @
                if (!searchText.includes(' ')) {
                    setMentionSearch(searchText);
                    setMentionStartIndex(lastAtIndex);
                    setShowMentionDropdown(true);
                    setSelectedMentionIndex(0);
                    return;
                }
            }
        }

        setShowMentionDropdown(false);
        setMentionStartIndex(-1);
    }, []);

    // Handle mention selection
    const handleMentionSelect = useCallback((member) => {
        if (mentionStartIndex === -1) return;

        const beforeMention = newComment.substring(0, mentionStartIndex);
        const afterMention = newComment.substring(
            mentionStartIndex + mentionSearch.length + 1
        );
        const newValue = `${beforeMention}@${member.name} ${afterMention}`;

        setNewComment(newValue);
        setShowMentionDropdown(false);
        setMentionStartIndex(-1);
        setMentionSearch('');

        // Focus back on input
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, [mentionStartIndex, mentionSearch, newComment]);

    // Handle keyboard navigation in mention dropdown
    const handleKeyDown = useCallback((e) => {
        if (!showMentionDropdown || filteredMembers.length === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedMentionIndex(prev =>
                prev < filteredMembers.length - 1 ? prev + 1 : 0
            );
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedMentionIndex(prev =>
                prev > 0 ? prev - 1 : filteredMembers.length - 1
            );
        } else if (e.key === 'Enter' && showMentionDropdown) {
            e.preventDefault();
            handleMentionSelect(filteredMembers[selectedMentionIndex]);
        } else if (e.key === 'Escape') {
            setShowMentionDropdown(false);
        }
    }, [showMentionDropdown, filteredMembers, selectedMentionIndex, handleMentionSelect]);

    // Parse message to highlight mentions
    const renderMessageWithMentions = (message) => {
        if (!message) return null;

        // Match @username pattern (username can contain letters, numbers, spaces)
        const mentionRegex = /@([A-Za-z0-9\s]+?)(?=\s@|\s|$)/g;
        const parts = [];
        let lastIndex = 0;
        let match;

        while ((match = mentionRegex.exec(message)) !== null) {
            // Add text before mention
            if (match.index > lastIndex) {
                parts.push(message.substring(lastIndex, match.index));
            }

            // Add mention with styling
            const mentionedName = match[1].trim();
            parts.push(
                <span key={match.index} className="mention-highlight">
                    @{mentionedName}
                </span>
            );

            lastIndex = match.index + match[0].length;
        }

        // Add remaining text
        if (lastIndex < message.length) {
            parts.push(message.substring(lastIndex));
        }

        return parts.length > 0 ? parts : message;
    };

    // Subscribe to real-time comment updates via Pusher
    useEffect(() => {
        if (!isOpen || !task?.id) return;

        const echo = getEcho();
        if (!echo) {
            console.warn('Echo not available for task comments');
            return;
        }

        console.log(`Subscribing to task.${task.id} channel for real-time comments`);

        const channel = echo.join(`task.${task.id}`);

        // Listen for new comments
        channel.listen('.task.comment.created', (e) => {
            console.log('Real-time comment received:', e);
            if (e.comment) {
                receiveRealtimeComment(e.comment);
            }
        });

        // Listen for deleted comments
        channel.listen('.task.comment.deleted', (e) => {
            console.log('Real-time comment deletion received:', e);
            if (e.comment_id && e.task_id) {
                removeRealtimeComment(e.comment_id, e.task_id);
            }
        });

        // Cleanup on unmount or when modal closes
        return () => {
            console.log(`Leaving task.${task.id} channel`);
            echo.leave(`task.${task.id}`);
        };
    }, [isOpen, task?.id, receiveRealtimeComment, removeRealtimeComment]);

    if (!isOpen || !currentTask) return null;

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    // Handle status change
    const handleStatusChange = async (newStatus) => {
        if (!currentTask?.id) return;
        try {
            await updateTaskStatus(currentTask.id, newStatus);
            setShowStatusDropdown(false);
            // Update local state immediately for instant UI feedback
            setCurrentTask(prev => ({ ...prev, status: newStatus }));
        } catch (error) {
            console.error('Failed to update status:', error);
        }
    };

    // Handle priority change
    const handlePriorityChange = async (newPriority) => {
        if (!currentTask?.id) return;
        try {
            await updateTaskPriority(currentTask.id, newPriority);
            setShowPriorityDropdown(false);
            // Update local state immediately for instant UI feedback
            setCurrentTask(prev => ({ ...prev, priority: newPriority }));
        } catch (error) {
            console.error('Failed to update priority:', error);
        }
    };

    // Toggle status dropdown
    const toggleStatusDropdown = (e) => {
        e.stopPropagation();
        setShowStatusDropdown(!showStatusDropdown);
        setShowPriorityDropdown(false);
    };

    // Toggle priority dropdown
    const togglePriorityDropdown = (e) => {
        e.stopPropagation();
        setShowPriorityDropdown(!showPriorityDropdown);
        setShowStatusDropdown(false);
    };

    const handleCommentSubmit = async (e) => {
        e.preventDefault();
        if (!newComment.trim() || isSubmitting) return;

        try {
            setIsSubmitting(true);
            const comment = await addComment(currentTask.id, newComment.trim());
            setNewComment('');

            // Add comment to paginated list immediately
            if (comment) {
                setPaginatedComments(prev => [...prev, comment]);
            }

            // Scroll to bottom to show new comment
            setTimeout(() => {
                scrollToBottom();
            }, 100);
        } catch (error) {
            console.error('Failed to add comment:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }).replace(/\//g, '/');
    };

    const getStatusIcon = (status) => {
        const option = STATUS_OPTIONS.find(opt => opt.value === status);
        return option?.icon || statusTodoIcon;
    };

    const getStatusLabel = (status) => {
        const option = STATUS_OPTIONS.find(opt => opt.value === status);
        return option?.label || status?.toUpperCase();
    };

    const getStatusClass = (status) => {
        const classMap = {
            todo: 'status-todo',
            in_progress: 'status-in-progress',
            done: 'status-done',
            on_hold: 'status-on-hold',
            reopen: 'status-reopen',
        };
        return classMap[status] || '';
    };

    const getPriorityLabel = (priority) => {
        const option = PRIORITY_OPTIONS.find(opt => opt.value === priority);
        return option?.label || priority?.toUpperCase() || '-';
    };

    const getPriorityClass = (priority) => {
        const classMap = {
            low: 'priority-low',
            medium: 'priority-medium',
            high: 'priority-high',
            urgent: 'priority-urgent',
        };
        return classMap[priority] || '';
    };

    return (
        <div className="task-details-modal-overlay" onClick={handleBackdropClick}>
            <div className="task-details-modal">
                {/* Header */}
                <div className="task-details-modal-header">
                    <div className="task-details-modal-title">
                        <span>Task Details</span>
                        <FaEye className="task-details-title-icon" />
                    </div>
                    <button className="task-details-modal-close" onClick={onClose}>
                        <FaTimes size={14} />
                    </button>
                </div>

                {/* Content */}
                <div className="task-details-modal-content">
                    {/* Left Section - Task Info */}
                    <div className="task-details-info">
                        {/* Task Name */}
                        <div className="task-details-row task-name-row">
                            <div className="task-details-label">
                                <img src={listChecksIcon} alt="" className="task-details-icon" />
                                <span>Task Name</span>
                            </div>
                            <div className="task-details-value task-name-value">
                                {currentTask.title}
                            </div>
                        </div>

                        {/* Description */}
                        <div className="task-details-description-box">
                            {currentTask.description || 'No description provided.'}
                        </div>

                        <div className="task-details-divider"></div>

                        {/* Project Name */}
                        <div className="task-details-row">
                            <div className="task-details-label">
                                <img src={stackSimpleIcon} alt="" className="task-details-icon" />
                                <span>Project Name</span>
                            </div>
                            <div className="task-details-value">
                                {activeProject?.title || activeProject?.name || '-'}
                            </div>
                        </div>

                        {/* Created by */}
                        <div className="task-details-row">
                            <div className="task-details-label">
                                <img src={userIcon} alt="" className="task-details-icon" />
                                <span>Created by</span>
                            </div>
                            <div className="task-details-value">
                                {activeProject?.owner?.name || '-'}
                            </div>
                        </div>

                        {/* Team Member (Assignee) */}
                        <div className="task-details-row">
                            <div className="task-details-label">
                                <img src={userIcon} alt="" className="task-details-icon" />
                                <span>Team Member</span>
                            </div>
                            <div className="task-details-value">
                                {currentTask.assignee?.name || 'Unassigned'}
                            </div>
                        </div>

                        {/* Status */}
                        <div className="task-details-row">
                            <div className="task-details-label">
                                <img src={clockIcon} alt="" className="task-details-icon" />
                                <span>Status</span>
                            </div>
                            <div className="task-details-value">
                                <div className="inline-dropdown-container" ref={statusDropdownRef}>
                                    <span
                                        className={`status-badge ${getStatusClass(currentTask.status)} ${currentTask.permissions?.can_update_status ? 'clickable' : ''}`}
                                        onClick={currentTask.permissions?.can_update_status ? toggleStatusDropdown : undefined}
                                    >
                                        <img src={getStatusIcon(currentTask.status)} alt="" className="status-icon" />
                                        {getStatusLabel(currentTask.status)}
                                        {currentTask.permissions?.can_update_status && <img src={downArrowIcon} alt="" className="dropdown-arrow" />}
                                    </span>
                                    {showStatusDropdown && (
                                        <div className="inline-dropdown-menu status-dropdown-menu">
                                            {STATUS_OPTIONS.map((option) => (
                                                <div
                                                    key={option.value}
                                                    className={`inline-dropdown-item ${currentTask.status === option.value ? 'active' : ''}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleStatusChange(option.value);
                                                    }}
                                                >
                                                    <span className={`status-badge ${getStatusClass(option.value)}`}>
                                                        <img src={option.icon} alt="" className="status-icon" />
                                                        {option.label}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Priority */}
                        <div className="task-details-row">
                            <div className="task-details-label">
                                <img src={caretDoubleUpIcon} alt="" className="task-details-icon" />
                                <span>Priority</span>
                            </div>
                            <div className="task-details-value">
                                <div className="inline-dropdown-container" ref={priorityDropdownRef}>
                                    <span
                                        className={`priority-badge ${getPriorityClass(currentTask.priority)} ${currentTask.permissions?.can_update_status ? 'clickable' : ''}`}
                                        onClick={currentTask.permissions?.can_update_status ? togglePriorityDropdown : undefined}
                                    >
                                        {getPriorityLabel(currentTask.priority)}
                                        {currentTask.permissions?.can_update_status && <img src={downArrowIcon} alt="" className="dropdown-arrow priority-arrow" />}
                                    </span>
                                    {showPriorityDropdown && (
                                        <div className="inline-dropdown-menu priority-dropdown-menu">
                                            {PRIORITY_OPTIONS.map((option) => (
                                                <div
                                                    key={option.value}
                                                    className={`inline-dropdown-item ${currentTask.priority === option.value ? 'active' : ''}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handlePriorityChange(option.value);
                                                    }}
                                                >
                                                    <span className={`priority-badge ${getPriorityClass(option.value)}`}>
                                                        {option.label}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Date */}
                        <div className="task-details-row">
                            <div className="task-details-label">
                                <img src={calendarDotsIcon} alt="" className="task-details-icon" />
                                <span>Date</span>
                            </div>
                            <div className="task-details-value task-date-value">
                                <img src={calendarDotsIcon} alt="" className="date-icon" />
                                <span>{formatDate(currentTask.start_date)}</span>
                                <img src={arrowRightIcon} alt="" className="date-arrow" />
                                <img src={calendarDotsIcon} alt="" className="date-icon" />
                                <span>{formatDate(currentTask.end_date || currentTask.due_date)}</span>
                            </div>
                        </div>

                        {/* Expected Days */}
                        <div className="task-details-row">
                            <div className="task-details-label">
                                <img src={hourglassLowIcon} alt="" className="task-details-icon" />
                                <span>Expected Days</span>
                            </div>
                            <div className="task-details-value">
                                {currentTask.expected_days || '-'}
                            </div>
                        </div>

                        {/* Files Attached */}
                        <div className="task-details-row">
                            <div className="task-details-label">
                                <img src={fileTextIcon} alt="" className="task-details-icon" />
                                <span>Files Attached</span>
                            </div>
                            <div className="task-details-value">
                                <button className="view-files-btn" onClick={() => setShowDocumentsModal(true)}>
                                    View Files Attached
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Right Section - Notes & Chat */}
                    <div className="task-details-chat">
                        <h3 className="task-details-chat-title">Notes & Chat</h3>

                        <div
                            className="task-details-chat-messages"
                            ref={messagesContainerRef}
                            onScroll={handleScroll}
                        >
                            {isLoadingComments && hasMore && (
                                <div className="loading-more-comments">
                                    Loading older messages...
                                </div>
                            )}
                            {paginatedComments.length === 0 ? (
                                <div className="task-details-chat-empty">
                                    No comments yet
                                </div>
                            ) : (
                                <>
                                    {paginatedComments.map(comment => (
                                        <div key={comment.id} className="task-details-chat-message">
                                            <div className="chat-message-avatar">
                                                {comment.user?.name?.charAt(0)?.toUpperCase() || '?'}
                                            </div>
                                            <div className="chat-message-content">
                                                <span className="chat-message-author">{comment.user?.name}</span>
                                                <p className="chat-message-text">{renderMessageWithMentions(comment.message)}</p>
                                            </div>
                                        </div>
                                    ))}
                                    <div ref={messagesEndRef} />
                                </>
                            )}
                        </div>

                        <form className="task-details-chat-input" onSubmit={handleCommentSubmit}>
                            <div className="chat-input-wrapper">
                                {/* Mention Dropdown */}
                                {showMentionDropdown && filteredMembers.length > 0 && (
                                    <div className="mention-dropdown">
                                        {filteredMembers.map((member, index) => (
                                            <div
                                                key={member.id}
                                                className={`mention-dropdown-item ${index === selectedMentionIndex ? 'selected' : ''}`}
                                                onClick={() => handleMentionSelect(member)}
                                            >
                                                <div className="mention-avatar">
                                                    {member.name?.charAt(0)?.toUpperCase() || '?'}
                                                </div>
                                                <div className="mention-info">
                                                    <span className="mention-name">{member.name}</span>
                                                    <span className="mention-email">{member.email}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <input
                                    ref={inputRef}
                                    type="text"
                                    placeholder={currentTask.permissions?.can_comment ? "Type @ to mention someone" : "Commenting disabled"}
                                    value={newComment}
                                    onChange={handleInputChange}
                                    onKeyDown={handleKeyDown}
                                    disabled={!currentTask.permissions?.can_comment}
                                />
                                <button
                                    type="submit"
                                    className="chat-send-btn"
                                    disabled={!newComment.trim() || isSubmitting || !currentTask.permissions?.can_comment}
                                >
                                    <img src={sendIcon} alt="Send" className="send-icon" />
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Uploaded Documents Modal */}
            <UploadedDocumentsModal
                isOpen={showDocumentsModal}
                onClose={() => setShowDocumentsModal(false)}
                task={currentTask}
                loading={taskDetailsLoading}
                attachments={currentTask?.attachments || []}
                onUpload={(newAttachment) => {
                    // Update local state immediately
                    setCurrentTask(prev => ({
                        ...prev,
                        attachments: [newAttachment, ...(prev.attachments || [])]
                    }));
                }}
                onDownload={async (attachment) => {
                    try {
                        // Use the api instance to include the auth token
                        const response = await api.get(`/v1/tasks/${currentTask.id}/attachments/${attachment.id}/download`, {
                            responseType: 'blob'
                        });

                        const fileName = attachment.upload?.original_filename || attachment.file_name || 'document';
                        const blob = new Blob([response], { type: response.type });
                        const blobUrl = window.URL.createObjectURL(blob);

                        const link = document.createElement('a');
                        link.href = blobUrl;
                        link.download = fileName;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(blobUrl);
                    } catch (error) {
                        console.error('Download failed:', error);
                        notify('error', 'Download failed', 'Could not download the file.');
                    }
                }}
                onDownloadAll={async () => {
                    try {
                        const response = await api.get(`/v1/tasks/${currentTask.id}/attachments/download-all`, {
                            responseType: 'blob'
                        });

                        const fileName = `task_${currentTask.id}_attachments.zip`;
                        const blob = new Blob([response], { type: 'application/zip' });
                        const blobUrl = window.URL.createObjectURL(blob);

                        const link = document.createElement('a');
                        link.href = blobUrl;
                        link.download = fileName;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(blobUrl);
                    } catch (error) {
                        console.error('Download All failed:', error);
                        notify('error', 'Download Failed', 'Could not prepare the ZIP file.');
                    }
                }}
                onView={(attachment) => {
                    const url = attachment.upload?.file_url;
                    if (url) window.open(url, '_blank');
                }}
            />
        </div>
    );
};

export default TaskDetailsModal;
